import {
  useState,
  useRef,
  useEffect,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { cn } from '@/utils'
import { Button, Spinner } from '@/components/ui'
import { Send, Square, Mic, MicOff, Paperclip, X } from 'lucide-react'
import { ATTACHMENT_ACCEPT } from '@/constants/attachments'

interface ChatInputProps {
  onSend: (message: string) => void
  onCancel?: () => void
  isLoading?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
  // Voice input (STT) props
  sttEnabled?: boolean
  isListening?: boolean
  voiceTranscript?: string
  onToggleListening?: () => void
  /** Hide the basic STT mic button (e.g., when voice conversation is active) */
  hideBasicSttMic?: boolean
  /** Optional element rendered to the left of the input (e.g., PTT button) */
  leftAddon?: ReactNode
  /** External message to populate (e.g., from suggested questions) */
  externalMessage?: string
  /** Callback when external message is consumed */
  onExternalMessageConsumed?: () => void
  /** Webchat attachments (chat_attachments feature) — opt-in per surface */
  attachmentsEnabled?: boolean
  stagedFile?: File | null
  onStageFile?: (file: File) => void
  onRemoveStagedFile?: () => void
  attachmentError?: string | null
}

export function ChatInput({
  onSend,
  onCancel,
  isLoading = false,
  disabled = false,
  placeholder = 'Type your message...',
  className,
  sttEnabled = false,
  isListening = false,
  voiceTranscript = '',
  onToggleListening,
  hideBasicSttMic = false,
  leftAddon,
  externalMessage,
  onExternalMessageConsumed,
  attachmentsEnabled = false,
  stagedFile = null,
  onStageFile,
  onRemoveStagedFile,
  attachmentError = null,
}: ChatInputProps) {
  const [message, setMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFilePicked = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && onStageFile) onStageFile(file)
    // Allow re-picking the same file after removal
    e.target.value = ''
  }

  // Update message when voice transcript changes
  // We update whenever voiceTranscript changes, regardless of isListening state,
  // because the final transcript may arrive just as isListening becomes false
  useEffect(() => {
    if (voiceTranscript) {
      console.debug('[ChatInput] Voice transcript update:', voiceTranscript)
      setMessage(voiceTranscript)
    }
  }, [voiceTranscript])

  // Handle external message (e.g., from suggested questions)
  useEffect(() => {
    if (externalMessage) {
      setMessage(externalMessage)
      onExternalMessageConsumed?.()
      // Focus the textarea so user can edit or submit
      textareaRef.current?.focus()
    }
  }, [externalMessage, onExternalMessageConsumed])

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px'
    }
  }, [message])

  // Focus on mount
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  const hasStagedFile = attachmentsEnabled && !!stagedFile

  const handleSubmit = () => {
    const trimmed = message.trim()
    if ((trimmed || hasStagedFile) && !isLoading && !disabled) {
      onSend(trimmed)
      setMessage('')
      // Reset height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const canSend = (message.trim().length > 0 || hasStagedFile) && !isLoading && !disabled

  return (
    <div className={cn('bg-background border-t border-border', className)}>
      {attachmentsEnabled && (stagedFile || attachmentError) && (
        <div className="px-4 pt-3">
          {stagedFile && (
            <span
              data-testid="chat-attach-chip"
              className="inline-flex items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-sm text-foreground"
            >
              <Paperclip className="w-4 h-4 shrink-0" />
              <span className="max-w-[16rem] truncate">{stagedFile.name}</span>
              <span className="text-muted-foreground text-xs">
                {(stagedFile.size / 1024).toFixed(0)} KB
              </span>
              <button
                type="button"
                data-testid="chat-attach-remove"
                onClick={onRemoveStagedFile}
                className="text-muted-foreground hover:text-foreground"
                title="Remove attachment"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {attachmentError && (
            <p data-testid="chat-attach-error" className="mt-1 text-sm text-destructive">
              {attachmentError}
            </p>
          )}
        </div>
      )}
      <div className="flex items-end gap-2 p-4">
        {/* Optional left addon (e.g., voice conversation PTT button) */}
        {leftAddon}

        {attachmentsEnabled && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              data-testid="chat-attach-input"
              onChange={handleFilePicked}
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isLoading}
              className="h-12 w-12 p-0 rounded-xl"
              title="Attach a file"
              data-testid="chat-attach-button"
            >
              <Paperclip className="w-5 h-5" />
            </Button>
          </>
        )}

        {/* Text input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={cn(
              'w-full resize-none rounded-xl border border-border',
              'bg-muted',
              'px-4 py-3 pr-12',
              'text-foreground',
              'placeholder:text-muted-foreground',
              'focus:outline-hidden focus:ring-2 focus:ring-ring focus:border-transparent',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-all duration-200'
            )}
          />

          {/* Character count (optional, shown when near limit) */}
          {message.length > 3000 && (
            <span
              className={cn(
                'absolute right-3 bottom-3 text-xs',
                message.length > 4000 ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {message.length}/4096
            </span>
          )}
        </div>

        {/* Mic button (when STT enabled and not hidden by voice conversation) */}
        {/* Always show stop button when listening, even if voice conversation is active - this is the escape hatch */}
        {sttEnabled && onToggleListening && (isListening || !hideBasicSttMic) && (
          <Button
            variant={isListening ? 'destructive' : 'secondary'}
            onClick={onToggleListening}
            className={cn('h-12 w-12 p-0 rounded-xl', isListening && 'animate-pulse')}
            title={isListening ? 'Stop listening' : 'Start voice input'}
          >
            {isListening ? (
              <MicOff className="w-5 h-5" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </Button>
        )}

        {/* Send/Cancel button */}
        {isLoading ? (
          <Button
            variant="secondary"
            onClick={onCancel}
            className="h-12 w-12 p-0 rounded-xl"
            title="Cancel"
          >
            <Square className="w-5 h-5" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canSend}
            className="h-12 w-12 p-0 rounded-xl"
            title="Send message"
          >
            {disabled ? (
              <Spinner />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

// Voice input variant (for future use)
interface VoiceChatInputProps {
  onStartRecording: () => void
  onStopRecording: () => void
  isRecording: boolean
  disabled?: boolean
  className?: string
}

export function VoiceChatInput({
  onStartRecording,
  onStopRecording,
  isRecording,
  disabled = false,
  className,
}: VoiceChatInputProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-center p-6 bg-background border-t border-border',
        className
      )}
    >
      <button
        onClick={isRecording ? onStopRecording : onStartRecording}
        disabled={disabled}
        className={cn(
          'w-16 h-16 rounded-full flex items-center justify-center transition-all',
          isRecording
            ? 'bg-destructive text-white animate-pulse'
            : 'bg-primary text-white hover:bg-primary/90',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {isRecording ? (
          <Square className="w-6 h-6" />
        ) : (
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
            />
          </svg>
        )}
      </button>
    </div>
  )
}
